import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { can, type MeDto, type Op, type PermCtx } from "@audit-plan/shared";
import { api, ApiError } from "../api/client";

interface AuthValue {
  me: MeDto | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** Stessa matrice permessi del backend (shared/domain.ts). Il server resta l'autorità. */
  can: (op: Op, ctx?: PermCtx) => boolean;
}
const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MeDto>("/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  const value: AuthValue = {
    me,
    loading: q.isLoading,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    logout: async () => {
      await api.post("/auth/logout");
      qc.clear();
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    can: (op, ctx) => can(me, op, ctx),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth fuori da AuthProvider");
  return v;
}
